import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { formatMoney } from 'features/company/companyUtils';
import { EmptyState, ErrorState, PageHeader, Skeleton, StatusChip } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime, localizedText } from 'shared/utilities/format';

export function CompanyPurchasesPage() {
  const { t, locale } = useI18n();
  const { companyId } = useCompany();
  const [page, setPage] = useState(1);
  const query = useQuery({
    queryKey: queryKeys.companyPurchases(page),
    queryFn: () => companyApi.purchases(companyId, { page, per_page: 15 }),
  });

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  const items = query.data?.items || [];
  return (
    <div className="list-page">
      <PageMeta titleKey="meta.companyPurchases" />
      <PageHeader
        title={t('company.purchases.title')}
        description={t('company.purchases.body')}
        actions={<Link className="btn btn-primary" to={paths.companyPlans}>{t('company.purchases.buyPlan')}</Link>}
      />
      {items.length === 0 ? (
        <EmptyState title={t('company.purchases.empty')} />
      ) : (
        <div className="record-table">
          {items.map((purchase) => (
            <article key={purchase.id} className="record-row">
              <div>
                <strong>{localizedText(purchase, locale, ['plan_name', 'name'])}</strong>
                <StatusChip tone={purchase.status}>{purchase.status_label || t(`company.purchase.statuses.${purchase.status}`)}</StatusChip>
              </div>
              <p>{formatMoney(purchase.total_minor, purchase.currency, locale)}</p>
              <p className="muted">{formatDateTime(purchase.created_at, locale)}</p>
              <Link className="btn btn-secondary" to={paths.companyPurchase(purchase.id)}>{t('actions.view')}</Link>
            </article>
          ))}
        </div>
      )}
      <Pagination meta={query.data?.meta} page={page} onPageChange={setPage} />
    </div>
  );
}
